import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { useNotifications } from "@itaas/core/notifications/useNotifications";
import {
  countUnreadLocalNotifications,
  listLocalNotificationsForUser,
  markAllLocalNotificationsRead,
  markLocalNotificationRead,
  type LocalNotification,
} from "@/core/notifications/local-notifications";

type InboxItem = {
  id: string;
  title: string;
  body: string;
  read: boolean;
  actionUrl?: string;
  source: "api" | "local";
};

export function HospitolNotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleSlugs = useMemo(
    () => (user?.roles ?? []).map((role) => role.slug),
    [user?.roles],
  );
  const { items, unreadCount, loading, error, markRead, markAllRead, refresh } =
    useNotifications(Boolean(user?.id));
  const [localTick, setLocalTick] = useState(0);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onUpdate = () => setLocalTick((value) => value + 1);
    window.addEventListener("hospitol:notifications", onUpdate);
    return () => window.removeEventListener("hospitol:notifications", onUpdate);
  }, []);

  const localItems = useMemo(() => {
    void localTick;
    if (!user?.id) return [];
    return listLocalNotificationsForUser({ userId: user.id, roleSlugs });
  }, [localTick, roleSlugs, user?.id]);

  const localUnread = useMemo(() => {
    void localTick;
    if (!user?.id) return 0;
    return countUnreadLocalNotifications({ userId: user.id, roleSlugs });
  }, [localTick, roleSlugs, user?.id]);

  const merged = useMemo((): InboxItem[] => {
    const apiItems: InboxItem[] = items.map((item) => ({
      id: `api:${item.id}`,
      title: item.title,
      body: item.body,
      read: item.status === "READ",
      actionUrl: typeof item.data?.actionUrl === "string" ? item.data.actionUrl : undefined,
      source: "api",
    }));
    const local: InboxItem[] = localItems.map((item: LocalNotification) => ({
      id: `local:${item.id}`,
      title: item.title,
      body: item.body,
      read: item.read,
      actionUrl: item.actionUrl,
      source: "local",
    }));
    return [...local, ...apiItems].slice(0, 30);
  }, [items, localItems]);

  const totalUnread = unreadCount + localUnread;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleItemClick = useCallback(
    async (item: InboxItem) => {
      if (item.source === "api") {
        await markRead(item.id.replace(/^api:/, ""));
      } else {
        markLocalNotificationRead(item.id.replace(/^local:/, ""));
      }
      setOpen(false);
      if (item.actionUrl) {
        navigate(item.actionUrl);
      }
    },
    [markRead, navigate],
  );

  const handleMarkAll = useCallback(async () => {
    if (user?.id) {
      markAllLocalNotificationsRead({ userId: user.id, roleSlugs });
    }
    await markAllRead();
    await refresh();
  }, [markAllRead, refresh, roleSlugs, user?.id]);

  if (!user) return null;

  return (
    <div className="position-relative" ref={rootRef}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary position-relative"
        aria-label="Notifications"
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden>🔔</span>
        {totalUnread > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="dropdown-menu show end-0 mt-1 shadow"
          style={{ minWidth: "22rem", maxHeight: "24rem", overflowY: "auto", right: 0 }}
        >
          <div className="dropdown-header d-flex justify-content-between align-items-center">
            <span>Notifications</span>
            {totalUnread > 0 && (
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void handleMarkAll()}>
                Tout lire
              </button>
            )}
          </div>

          {loading && merged.length === 0 && (
            <div className="dropdown-item-text text-muted small">Chargement…</div>
          )}

          {error && merged.length === 0 && (
            <div className="dropdown-item-text text-muted small">
              Plateforme notifications hors ligne — affichage local uniquement.
            </div>
          )}

          {!loading && merged.length === 0 && (
            <div className="dropdown-item-text text-muted small">Aucune notification</div>
          )}

          {merged.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dropdown-item text-wrap ${item.read ? "text-muted" : "fw-semibold"}`}
              onClick={() => void handleItemClick(item)}
            >
              <div>{item.title}</div>
              <div className="small text-muted">{item.body}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
